/**
 * MomentsStrip — candid camp photos that break the rigid grid cadence.
 *
 * Sits directly under the All-Inclusive section (same slate background;
 * top padding tightened so the two sections feel like one beat).
 *
 *   Desktop  ── one wide hero photo on the left with a caption-on-image
 *               overlay, plus two stacked portraits on the right (also
 *               with their own caption overlays).
 *   Mobile   ── hero photo full-width up top, then the two portraits in
 *               a side-by-side 2-column grid below so the whole strip
 *               fits comfortably without scrolling forever.
 *
 * Each image gets its own short tag + one-line caption, in the same
 * style as the first photo (yellow dash + uppercase eyebrow → white
 * caption). All Cloudinary URLs use `f_auto,q_auto`.
 */
const PHOTOS = [
  {
    src: 'https://res.cloudinary.com/dfa8raimq/image/upload/f_auto,q_auto/v1782235441/_28A4210_1_nmvcxo.jpg',
    alt: 'Mentor working with campers on a hands-on project at Ready Set STEM',
    tag: 'Real moments',
    caption: 'Hands-on every hour. No worksheets, no waiting.',
  },
  {
    src: 'https://res.cloudinary.com/dfa8raimq/image/upload/f_auto,q_auto/v1782419623/_28A4368_kignbt.jpg',
    alt: 'Camper deep in focus building their project',
    tag: 'In the lab',
    caption: 'Curious minds, careful hands.',
  },
  {
    src: 'https://res.cloudinary.com/dfa8raimq/image/upload/f_auto,q_auto/v1782419606/_28A4346_ks46sv.jpg',
    alt: 'Mentor coaching campers through a STEM problem',
    tag: 'Team energy',
    caption: 'Mentors right beside them, all day.',
  },
];

function PhotoCard({ src, alt, tag, caption, className = '' }) {
  return (
    <div
      className={`relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg ring-1 ring-rsc-card-border ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent p-3 sm:p-5">
        <span className="inline-flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[11px] uppercase tracking-widest font-extrabold text-rsc-yellow">
          <span className="inline-block h-[2px] sm:h-[3px] w-4 sm:w-6 bg-rsc-yellow rounded-full" aria-hidden />
          {tag}
        </span>
        <p className="mt-1 sm:mt-1.5 text-white text-[12px] sm:text-base font-bold leading-snug max-w-md">
          {caption}
        </p>
      </div>
    </div>
  );
}

export default function MomentsStrip() {
  const [main, second, third] = PHOTOS;
  return (
    <section
      className="bg-rsc-slate pt-2 sm:pt-4 pb-12 sm:pb-16 font-poppins"
      data-testid="moments-strip"
    >
      <div className="container-app">
        <div className="grid md:grid-cols-[1.6fr_1fr] gap-3 sm:gap-5 max-w-6xl mx-auto">

          {/* ── Hero photo (left on desktop, full-width on mobile) ── */}
          <PhotoCard
            {...main}
            className="aspect-[16/10] sm:aspect-[4/3] md:aspect-auto md:min-h-[460px]"
          />

          {/* ── Two smaller photos.
              Desktop: stacked vertically.
              Mobile: side-by-side in a 2-col grid so the strip ends
              quickly instead of consuming three full screens. ── */}
          <div className="grid grid-cols-2 md:grid-cols-1 md:grid-rows-2 gap-3 sm:gap-5">
            <PhotoCard
              {...second}
              className="aspect-[4/5] sm:aspect-[4/3] md:aspect-auto md:min-h-[220px]"
            />
            <PhotoCard
              {...third}
              className="aspect-[4/5] sm:aspect-[4/3] md:aspect-auto md:min-h-[220px]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
